import "@/App.css";
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/lib/auth";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { GoogleAuthGate } from "@/components/GoogleAuthGate";
import { AppShell } from "@/components/layout/AppShell";

// Landing is the entry route — keep it eager so it paints instantly with no suspense flash.
import Landing from "@/pages/Landing";

// Everything else is code-split so the landing page ships a minimal bundle.
const Login = lazy(() => import("@/pages/auth/Login"));
const Register = lazy(() => import("@/pages/auth/Register"));
const Verify = lazy(() => import("@/pages/auth/Verify"));
const Forgot = lazy(() => import("@/pages/auth/Forgot"));
const Reset = lazy(() => import("@/pages/auth/Reset"));
const Privacy = lazy(() => import("@/pages/legal/Privacy"));
const Terms = lazy(() => import("@/pages/legal/Terms"));

const Dashboard = lazy(() => import("@/pages/Dashboard"));
const CaseLibrary = lazy(() => import("@/pages/CaseLibrary"));
const CaseDetail = lazy(() => import("@/pages/CaseDetail"));
const CreateCustomCase = lazy(() => import("@/pages/CreateCustomCase"));
const AISession = lazy(() => import("@/pages/AISession"));
const Feedback = lazy(() => import("@/pages/Feedback"));
const Sessions = lazy(() => import("@/pages/Sessions"));
const Revision = lazy(() => import("@/pages/Revision"));
const Bookmarks = lazy(() => import("@/pages/Bookmarks"));
const Notebook = lazy(() => import("@/pages/Notebook"));
const MockExamIntro = lazy(() => import("@/pages/MockExamIntro"));
const MockExamRunner = lazy(() => import("@/pages/MockExamRunner"));
const MockExamResult = lazy(() => import("@/pages/MockExamResult"));
const Subscribe = lazy(() => import("@/pages/Subscribe"));
const Credits = lazy(() => import("@/pages/Credits"));
const CreditsReceipt = lazy(() => import("@/pages/CreditsReceipt"));
const Profile = lazy(() => import("@/pages/Profile"));
const Connect = lazy(() => import("@/pages/Connect"));
const Friends = lazy(() => import("@/pages/Friends"));
const AdminDashboard = lazy(() => import("@/pages/admin/AdminDashboard"));
const PartnerDashboard = lazy(() => import("@/pages/partner/PartnerDashboard"));

function RouteFallback() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background" data-testid="route-loading">
      <Loader2 className="h-8 w-8 animate-spin text-primary" />
    </div>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <GoogleAuthGate>
          <Suspense fallback={<RouteFallback />}>
          <Routes>
            {/* Public */}
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/verify" element={<Verify />} />
            <Route path="/forgot" element={<Forgot />} />
            <Route path="/reset" element={<Reset />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />

            {/* Authenticated app */}
            <Route element={<ProtectedRoute />}>
              <Route element={<AppShell />}>
                <Route path="/app" element={<Dashboard />} />
                <Route path="/app/cases" element={<CaseLibrary />} />
                <Route path="/app/cases/new" element={<CreateCustomCase />} />
                <Route path="/app/cases/:id" element={<CaseDetail />} />
                <Route path="/app/session/:id" element={<AISession />} />
                <Route path="/app/feedback/:id" element={<Feedback />} />
                <Route path="/app/history" element={<Sessions />} />
                <Route path="/app/revision" element={<Revision />} />
                <Route path="/app/bookmarks" element={<Bookmarks />} />
                <Route path="/app/notebook" element={<Notebook />} />
                <Route path="/app/mock-exam" element={<MockExamIntro />} />
                <Route path="/app/mock-exam/:id" element={<MockExamRunner />} />
                <Route path="/app/mock-exam/:id/result" element={<MockExamResult />} />
                <Route path="/app/connect" element={<Connect />} />
                <Route path="/app/friends" element={<Friends />} />
                <Route path="/app/subscribe" element={<Subscribe />} />
                <Route path="/app/credits" element={<Credits />} />
                <Route path="/app/credits/receipt" element={<CreditsReceipt />} />
                <Route path="/app/profile" element={<Profile />} />
                <Route path="/app/admin" element={<AdminDashboard />} />
                <Route path="/app/partner" element={<PartnerDashboard />} />
              </Route>
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </Suspense>
          </GoogleAuthGate>
        </BrowserRouter>
        <Toaster position="top-center" richColors closeButton />
      </AuthProvider>
    </div>
  );
}

export default App;
